import styled from "styled-components";
import { Field, Form, Formik } from "formik";
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Button,
  Input,
  Label,
  Checkbox,
} from "@fluentui/react-components";
import { Dismiss24Regular } from "@fluentui/react-icons";
import React, { useContext, useState } from "react";
import { ColumnOptionsContext } from "./ColumnOptions";

type Props = {
  close: () => void;
  userAppSettingId: number | null;
};

export const ViewDialog = ({ close, userAppSettingId }: Props) => {
  const {
    views,
    selectedView,
    onCreateView,
    onUpdateView,
    isViewLoading,
    strings,
    id,
  } = useContext(ColumnOptionsContext);
  const [didTrySubmit, setDidTrySubmit] = useState(false);

  const isEdit = userAppSettingId !== null;

  const isLoading = isViewLoading;

  const onSubmit = (data: any) => {
    // trim data before storing
    data.userAppSettingName = data.userAppSettingName.trim();
    data.userAppSettingDescription = data.userAppSettingDescription.trim();
    if (!isEdit) {
      onCreateView(data);
    }
    //
    else {
      onUpdateView(data);
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(_, data) => !data.open && close()}
      modalType="modal"
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                aria-label={strings.closeButtonLabel}
                icon={<Dismiss24Regular />}
                onClick={close}
              />
            }
          >
            {isEdit
              ? strings.editViewDialogLabel
              : strings.createViewDialogLabel}
          </DialogTitle>
          <DialogContent>
            <Formik
              initialValues={
                isEdit
                  ? selectedView
                  : {
                      userAppSettingName: "",
                      userAppSettingDescription: "",
                      isDefault: false,
                    }
              }
              onSubmit={onSubmit}
              validateOnChange={didTrySubmit}
              validateOnBlur={didTrySubmit}
            >
              {({ submitForm }) => {
                return (
                  <Form>
                    <Field
                      name="userAppSettingName"
                      validate={(value: any) => {
                        if (value.trim() === "")
                          return strings.requiredErrorMessage;
                        let doesExist = !!views.find(
                          (s) => s.userAppSettingName === value.trim()
                        );
                        if (isEdit) {
                          doesExist = !!views
                            .filter(
                              (s) =>
                                s.userAppSettingId !==
                                selectedView?.userAppSettingId
                            )
                            .find((s) => s.userAppSettingName === value);
                        }
                        if (doesExist) {
                          return strings.viewExistsErrorMessage;
                        }
                        return undefined;
                      }}
                    >
                      {({
                        field: { onChange, value, ...field },
                        meta: { error },
                      }: any) => {
                        return (
                          <FieldContainer>
                            <Label required>
                              {strings.viewDialogNameFieldLabel}
                            </Label>
                            <Input
                              autoFocus
                              maxLength={128}
                              value={value}
                              onChange={(e) => onChange(e)}
                              id={id ? `${id}-field-name` : undefined}
                              {...field}
                            />
                            {error && <ErrorMessage>{error}</ErrorMessage>}
                          </FieldContainer>
                        );
                      }}
                    </Field>
                    <Field name="userAppSettingDescription">
                      {({
                        field: { onChange, value, ...field },
                        meta: { error },
                      }: any) => {
                        return (
                          <FieldContainer>
                            <Label>
                              {strings.viewDialogDescriptionFieldLabel}
                            </Label>
                            <Input
                              maxLength={128}
                              value={value}
                              onChange={(e) => onChange(e)}
                              id={id ? `${id}-field-desc` : undefined}
                              {...field}
                            />
                            {error && <ErrorMessage>{error}</ErrorMessage>}
                          </FieldContainer>
                        );
                      }}
                    </Field>
                    <Field name="isDefault" type="checkbox">
                      {({
                        field: { value, ...field },
                        form: { setFieldValue },
                      }: any) => {
                        return (
                          <FieldContainer>
                            <Checkbox
                              label={strings.viewDialogFavoriteFieldLabel}
                              checked={value}
                              onChange={(_, data) =>
                                setFieldValue(field.name, data.checked)
                              }
                              id={id ? `${id}-checkbox-star` : undefined}
                              {...field}
                            />
                          </FieldContainer>
                        );
                      }}
                    </Field>
                    <DialogActions>
                      <Button
                        appearance="secondary"
                        onClick={close}
                        disabled={isLoading}
                      >
                        {strings.cancelButtonLabel}
                      </Button>
                      <Button
                        appearance="primary"
                        onClick={() => {
                          setDidTrySubmit(true);
                          submitForm();
                        }}
                        disabled={isLoading}
                        id={id ? `${id}-btn-save` : undefined}
                      >
                        {strings.viewDialogSaveButtonLabel}
                      </Button>
                    </DialogActions>
                  </Form>
                );
              }}
            </Formik>
          </DialogContent>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

const FieldContainer = styled.div`
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ErrorMessage = styled.span`
  color: ${(props) => props.theme.palette.redDark};
  font-size: 12px;
`;
